/**
 * Shared reading of the ArgoCD Application API.
 *
 * Two screens consume the very same payload: the environments card
 * (`EnvironmentsCard`), which lists one Application per branch of a
 * project, and the per-branch page (`BranchPage`), which opens exactly one of
 * them. The Application shape, the mapping onto an environment, the status
 * vocabulary and the authenticated call itself therefore live here rather than
 * being duplicated on both sides and drifting apart — the same reasoning that
 * put the resource-tree reading in `components/argocd/pods.ts`.
 *
 * Everything below is a plain function: no hook, no translation ref, no config
 * lookup. That is what lets the tests call it directly, and it is why the two
 * label helpers take a `t` function as an argument instead of reaching for one.
 */

import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import type { OpenIdConnectApi } from '@backstage/core-plugin-api';
import type { StatusChipStatus } from '../ui';
import { APP_BRANCH_LABEL, applicationUrlOf } from '../shared';
import { compareBranches } from './branchKind';

/**
 * ArgoCD health and sync statuses to message keys.
 *
 * These two cannot be checked against a union the way the CI statuses can: the
 * CI status is normalised by our own backend into a closed set, while these
 * arrive verbatim from the ArgoCD API as free-form strings. The lists below are
 * ArgoCD's documented values, but nothing guarantees they stay exhaustive —
 * hence the fallback in {@link healthLabel} and {@link syncLabel}.
 */
export const HEALTH_LABEL_KEYS = {
  Healthy: 'health.healthy',
  Progressing: 'health.progressing',
  Degraded: 'health.degraded',
  Suspended: 'health.suspended',
  Missing: 'health.missing',
  Unknown: 'health.unknown',
} as const;

export const SYNC_LABEL_KEYS = {
  Synced: 'sync.synced',
  OutOfSync: 'sync.outOfSync',
  Unknown: 'sync.unknown',
} as const;

/**
 * One source of an Application (ArgoCD v3.5.3 `ApplicationSource`), the
 * fields read here. `ref` names a source that only lends its files to
 * another one (`$<ref>/…` in a `valueFiles` entry) and renders nothing.
 */
export interface ArgoApplicationSource {
  repoURL?: string;
  targetRevision?: string;
  path?: string;
  ref?: string;
}

/**
 * What ArgoCD records the sources and revisions of a deployment with, in
 * either shape — `spec` with `status.sync`, a `status.history` entry, an
 * operation's `syncResult` or `operation.sync`. A single-source Application
 * fills `source` and `revision`; a multi-source one fills `sources` and
 * `revisions`, one revision per source, at the same index.
 */
export interface ArgoSourcesAndRevisions {
  source?: ArgoApplicationSource | null;
  revision?: string;
  sources?: ArgoApplicationSource[] | null;
  revisions?: string[] | null;
}

/** One entry of `status.history`, as ArgoCD sends it. */
export interface ArgoRevisionHistory extends ArgoSourcesAndRevisions {
  id?: number;
  deployedAt?: string;
  deployStartedAt?: string;
}

/**
 * The subset of an ArgoCD Application these screens read. The API returns a lot
 * more; only what we consume is declared.
 *
 * Every field below `metadata.name` is optional because the list endpoint and
 * the single-Application endpoint do not return the same depth of status, and
 * because an Application that has never synced carries neither history nor
 * revision.
 */
export interface ArgoApplication {
  metadata: {
    name: string;
    /**
     * The namespace of the Application object (ArgoCD's own, `argocd`, unless
     * Applications in any namespace are enabled). With the name, what tells
     * two Applications apart when two lists are merged (`projectApplications.ts`).
     */
    namespace?: string;
    /**
     * Set by the Kubernetes API server when the Application is created, and
     * never reused: a branch deleted and pushed again gets a new one. What
     * the « environment ready » banner's dismissal is remembered by
     * (`serviceProgress.ts`).
     */
    uid?: string;
    /** Set by the Kubernetes API server when the Application is created. */
    creationTimestamp?: string;
    /**
     * Set by the Kubernetes API server once the Application is being deleted
     * and its finalizer (`resources-finalizer.argocd.argoproj.io`) holds it
     * while its resources go. Part of the list answer too: ArgoCD's List
     * returns each Application whole (a DeepCopy, v3.5.3).
     */
    deletionTimestamp?: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
    /**
     * Set by the Kubernetes API server on every write of the Application —
     * its status included. What tells two reads of it apart in time
     * ({@link isStaleApplication}).
     */
    resourceVersion?: string;
  };
  spec?: {
    /** The AppProject — ArgoCD's terminal must be given it (`projectName`). */
    project?: string;
    /** Single-source Application. */
    source?: ArgoApplicationSource | null;
    /**
     * Multi-source Application: the project's chart, then the appenv
     * repository it takes its values from (`ref: appenv`) — see
     * {@link projectSource}.
     */
    sources?: ArgoApplicationSource[] | null;
    destination?: { namespace?: string };
  };
  status?: {
    sync?: {
      status?: string;
      revision?: string;
      /**
       * For a multi-source one: aligned on `comparedTo.sources`, the sources
       * the comparison was made with (v3.5.3 `CompareAppState`).
       */
      revisions?: string[] | null;
      comparedTo?: {
        source?: ArgoApplicationSource | null;
        sources?: ArgoApplicationSource[] | null;
      };
    };
    health?: { status?: string };
    history?: ArgoRevisionHistory[] | null;
    /**
     * ArgoCD's `ApplicationSummary`: among other things, the container images
     * of the Application's live resources. Stored on the Application, so in
     * the list answer as well as the Get (no `fields=` trims it).
     */
    summary?: { images?: string[] | null };
    /**
     * The last operation — the one in progress, or the last one completed
     * (ArgoCD v3.5.3 `OperationState`). What the failing-deployment banner
     * reads (`syncFailure.ts`).
     */
    operationState?: ArgoOperationState;
  };
}

/** One entry of `operationState.syncResult.resources` (`ResourceResult`). */
export interface ArgoResourceResult {
  kind?: string;
  name?: string;
  namespace?: string;
  /** `Synced`, `SyncFailed`, `Pruned`, `PruneSkipped`; empty for a hook. */
  status?: string;
  message?: string;
  /** `PreSync`, `Sync`, `PostSync`, `SyncFail`…; empty for a non-hook. */
  hookType?: string;
  /** `Running`, `Succeeded`, `Failed`, `Error`, `Terminating`. */
  hookPhase?: string;
  syncPhase?: string;
}

/**
 * `status.operationState` of an Application (ArgoCD v3.5.3 `OperationState`).
 * `phase` is gitops-engine's `OperationPhase`: `Running`, `Terminating`,
 * `Failed`, `Error`, `Succeeded`.
 */
export interface ArgoOperationState {
  phase?: string;
  message?: string;
  /** The retries made so far — 0 (absent) on the first attempt. */
  retryCount?: number;
  startedAt?: string;
  finishedAt?: string;
  operation?: {
    /** `revision` cleared by the controller on a retry with `refresh`. */
    sync?: ArgoSourcesAndRevisions;
    retry?: { limit?: number };
  };
  syncResult?: ArgoSourcesAndRevisions & {
    resources?: ArgoResourceResult[] | null;
  };
}

export interface ProjectEnvironment {
  appName: string;
  branch: string;
  syncStatus: string;
  healthStatus: string;
  url?: string;
  /** Namespace the branch is deployed into — the Grafana dashboard filter. */
  namespace?: string;
  /**
   * Browsable URL of the project on its forge, i.e. the clone URL without
   * `.git`. Its host is what decides whether the CI status is read from GitLab
   * pipelines or GitHub Actions runs.
   */
  repoUrl?: string;
  /** When the Application was created — the environment's age. */
  createdAt?: string;
  /**
   * When the revision deployed now was first deployed — see
   * {@link revisionDeployedAt}. What « inactive » is measured from.
   */
  revisionDeployedAt?: string;
  /**
   * When the Application started being deleted, if it is — one of the
   * signals of the « being deleted » state (`branchDeletion.ts`).
   */
  deletionTimestamp?: string;
}

/**
 * Maps an ArgoCD health status onto the semantic colours of Backstage UI.
 *
 * ArgoCD's health model has exactly six statuses — Healthy, Progressing,
 * Degraded, Suspended, Missing, Unknown — and this switch is written against
 * that list, not against a guess. `Degraded` is a failure state there, the one
 * ArgoCD paints red in its own UI, so it belongs with `danger`; it sat in
 * `warning` next to `Progressing`, which understated a broken environment as
 * "still settling". `Suspended` and `Unknown` fall through to `secondary`,
 * which is what neither-good-nor-bad should look like.
 *
 * The `status` parameter stays a plain string because the value arrives
 * verbatim from the ArgoCD API: an upgrade that adds a seventh status must land
 * in `default`, not fail to compile.
 */
export function healthColor(
  status: string,
): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
  switch (status) {
    case 'Healthy':
      return 'success';
    // In progress is drawn in the info tone everywhere, as by StatusChip.
    case 'Progressing':
      return 'info';
    case 'Degraded':
    case 'Missing':
      return 'danger';
    default:
      return 'secondary';
  }
}

/**
 * An ArgoCD health status as a `StatusChip` state, matching the colour
 * {@link healthColor} gives it: `Degraded` and `Missing` are `failed` (danger,
 * for the reason stated there) and not `degraded`, whose warning tone would
 * understate a broken environment again. `Progressing` is the info tone, as in
 * {@link healthColor}: « in progress » is drawn in info everywhere.
 * `Suspended`, `Unknown` and any status ArgoCD adds later are `unknown`, the chip's neutral tone — `secondary` in {@link healthColor}.
 */
export function healthChipStatus(status: string): StatusChipStatus {
  switch (status) {
    case 'Healthy':
      return 'healthy';
    case 'Progressing':
      return 'progressing';
    case 'Degraded':
    case 'Missing':
      return 'failed';
    default:
      return 'unknown';
  }
}

/**
 * An ArgoCD sync status as a `StatusChip` state: `Synced` healthy,
 * `OutOfSync` degraded — what is live differs from Git, something to look at
 * rather than a failure (the health says whether it is broken) — anything
 * else unknown.
 */
export function syncChipStatus(status: string): StatusChipStatus {
  switch (status) {
    case 'Synced':
      return 'healthy';
    case 'OutOfSync':
      return 'degraded';
    default:
      return 'unknown';
  }
}

/** A decimal `resourceVersion`, or undefined for any other form. */
function decimalVersion(app: ArgoApplication): string | undefined {
  const version = app.metadata.resourceVersion;
  return version && /^[0-9]+$/.test(version)
    ? version.replace(/^0+(?=.)/, '')
    : undefined;
}

/** The id of the last entry of `status.history`, ArgoCD's deployment count. */
function lastHistoryId(app: ArgoApplication): number | undefined {
  const history = app.status?.history;
  return history && history.length > 0
    ? history[history.length - 1].id
    : undefined;
}

/**
 * Whether `candidate` is a state of the Application no newer than `known` —
 * a list ArgoCD answered from its informer cache after a direct GET, or a
 * read that left before another and answered after it. A screen holding
 * `known` keeps it then.
 *
 * Judged on `metadata.resourceVersion` when both carry a decimal one: the
 * API server gives the object a new one on every write — a status update
 * included — and an etcd-backed server takes it from etcd's revision, which
 * only grows. Kubernetes calls the value opaque, hence the decimal check;
 * an equal one is the same write, nothing new. Without it, only what grows
 * for sure is compared — the id of the last deployment (`status.history`),
 * then the start of the last operation — and a candidate older on neither
 * is taken: the order of arrival decides, as it did before this check.
 */
export function isStaleApplication(
  candidate: ArgoApplication,
  known: ArgoApplication,
): boolean {
  const version = decimalVersion(candidate);
  const knownVersion = decimalVersion(known);
  if (version !== undefined && knownVersion !== undefined) {
    // Compared as decimal strings without leading zeros: no precision lost.
    return version.length === knownVersion.length
      ? version <= knownVersion
      : version.length < knownVersion.length;
  }
  const id = lastHistoryId(candidate);
  const knownId = lastHistoryId(known);
  if (id !== undefined && knownId !== undefined && id !== knownId) {
    return id < knownId;
  }
  const started = Date.parse(candidate.status?.operationState?.startedAt ?? '');
  const knownStarted = Date.parse(
    known.status?.operationState?.startedAt ?? '',
  );
  if (!Number.isNaN(started) && !Number.isNaN(knownStarted)) {
    return started < knownStarted;
  }
  return false;
}

/**
 * The container images ArgoCD reports for the Application, deduplicated and
 * in ArgoCD's order. The list holds more than Odoo's (init container,
 * database, Mailpit…); which one is the project's is decided by `rankImages`
 * in `images.ts`.
 */
export function deployedImages(app: ArgoApplication): string[] {
  return [...new Set(app.status?.summary?.images ?? [])];
}

/** The project's source of a deployment record, and its revision. */
export interface ProjectSource {
  source?: ArgoApplicationSource;
  revision?: string;
}

/**
 * The PROJECT's source — the project repository, its chart and its code —
 * and the revision of it, out of one record in either shape
 * ({@link ArgoSourcesAndRevisions}). The rule:
 *
 *  - `sources` non-empty: the record is multi-source, whatever `source`
 *    holds — ArgoCD's own test (v3.5.3 `HasMultipleSources`:
 *    `len(spec.Sources) > 0`), and a must for the status records, which
 *    carry a `source` even then (`RevisionHistory.Source` and
 *    `SyncOperationResult.Source` are not pointers: both are serialized as
 *    `{repoURL: ""}`). The project's is the
 *    first entry without `ref` — the appenv repository is a `ref` source
 *    (`ref: appenv`), whatever its position; its revision is the
 *    `revisions` entry at the same index. Neither when every entry is a
 *    `ref`.
 *  - otherwise `source` and `revision`, as they are.
 */
export function pickProjectSource(
  record: ArgoSourcesAndRevisions | null | undefined,
): ProjectSource {
  const sources = record?.sources ?? [];
  if (sources.length === 0) {
    return {
      source: record?.source ?? undefined,
      revision: record?.revision,
    };
  }
  const index = sources.findIndex(source => !source.ref);
  return index < 0
    ? {}
    : { source: sources[index], revision: record?.revisions?.[index] };
}

/**
 * The project's source of the Application (`spec.source` or
 * `spec.sources`, {@link pickProjectSource}) and the revision it is synced
 * to — `status.sync.revision`, or the `status.sync.revisions` entry of the
 * project's source. That entry is looked up in `status.sync.comparedTo.
 * sources`, which `revisions` is aligned on (v3.5.3 `CompareAppState`
 * writes both from the same list), and in `spec.sources` only when the
 * comparison names no sources.
 */
export function projectSource(app: ArgoApplication): ProjectSource {
  const sync = app.status?.sync;
  const { source } = pickProjectSource({
    source: app.spec?.source,
    sources: app.spec?.sources,
  });
  const { revision } = pickProjectSource({
    source: app.spec?.source,
    sources: sync?.comparedTo?.sources ?? app.spec?.sources,
    revision: sync?.revision,
    revisions: sync?.revisions,
  });
  return { source, revision };
}

/** {@link projectSource} of one `status.history` entry. */
export function historyProjectSource(
  entry: ArgoRevisionHistory,
): ProjectSource {
  return pickProjectSource(entry);
}

/**
 * When the revision at the end of `status.history` was FIRST deployed: the
 * `deployedAt` of the oldest entry of the trailing run that carries that same
 * revision. Undefined for an Application that has never been deployed.
 *
 * Not simply the last entry's `deployedAt`. ArgoCD appends an entry on every
 * successful FULL sync (v3.5.3 controller/sync.go: only when the operation
 * names no resource), whatever its revision. Self-heal does not add one — it
 * syncs the out-of-sync resources by name — but two paths do re-deploy the
 * SAME commit: a manual full Sync, and an auto-sync re-run because the
 * Application's source changed (e.g. an ApplicationSet template change)
 * without a new commit — `alreadyAttemptedSync` compares the source too.
 * Neither is work on the branch. A new revision is what a push produces, so
 * it is what « last activity » means here.
 *
 * The revision compared is the PROJECT's ({@link historyProjectSource}): on
 * a multi-source Application, a commit of the appenv repository alone — a
 * configuration change of the environment — re-deploys the same project
 * commit, and is not work on the branch either.
 *
 * ArgoCD keeps only the last `revisionHistoryLimit` entries (10 by default):
 * when they all carry the current revision, the oldest one kept is returned.
 * That date is later than the real one, so the idle time is under-estimated
 * and an environment is never called inactive too early.
 */
export function revisionDeployedAt(
  history: ArgoRevisionHistory[] | null | undefined,
): string | undefined {
  if (!history || history.length === 0) {
    return undefined;
  }
  const revision = (entry: ArgoRevisionHistory) =>
    historyProjectSource(entry).revision;
  const current = revision(history[history.length - 1]);
  let first = history.length - 1;
  while (first > 0 && revision(history[first - 1]) === current) {
    first -= 1;
  }
  return history[first].deployedAt;
}

/**
 * The branch of an environment Application: its `APP_BRANCH_LABEL`, set by
 * the applicationset, else the `targetRevision` of its project source should
 * the label ever go missing. One answer for {@link toEnvironment} and
 * {@link findApplicationForBranch}, which must agree (see the latter).
 */
export function applicationBranchOf(app: ArgoApplication): string | undefined {
  return (
    app.metadata.labels?.[APP_BRANCH_LABEL] ??
    projectSource(app).source?.targetRevision
  );
}

export function toEnvironment(app: ArgoApplication): ProjectEnvironment {
  const { source } = projectSource(app);
  const repoURL = source?.repoURL;

  return {
    appName: app.metadata.name,
    branch: applicationBranchOf(app) ?? '?',
    syncStatus: app.status?.sync?.status ?? 'Unknown',
    healthStatus: app.status?.health?.status ?? 'Unknown',
    url: applicationUrlOf(app),
    // Read from the Application rather than rebuilt from a naming convention:
    // the applicationset owns both, and reading them keeps the links correct if
    // the convention ever changes.
    namespace: app.spec?.destination?.namespace,
    repoUrl: repoURL?.replace(/\.git$/, ''),
    createdAt: app.metadata.creationTimestamp,
    revisionDeployedAt: revisionDeployedAt(app.status?.history),
    deletionTimestamp: app.metadata.deletionTimestamp,
  };
}

/**
 * The Application of one branch, out of everything the `waadoo.ovh/project`
 * selector returned.
 *
 * Matched on the *data* and never on the name: the Application name is composed
 * by the applicationset, so rebuilding `odoo-<project>-<branch>` here would
 * break the day that template changes — the same shortcut {@link toEnvironment}
 * refuses to take for the namespace and the repository URL.
 *
 * The label-then-targetRevision fallback is deliberately identical to the one
 * in {@link toEnvironment}: the card names the row and the page looks it up, so
 * the two must answer "which branch is this?" the same way or a link built by
 * the card would land on a page that finds nothing.
 */
export function findApplicationForBranch(
  apps: ArgoApplication[],
  branch: string,
): ArgoApplication | undefined {
  return apps.find(app => applicationBranchOf(app) === branch);
}

/** One entry of the per-branch page's branch switcher. */
export interface BranchOption {
  branch: string;
  /** ArgoCD's health status, verbatim — what {@link healthColor} paints. */
  health: string;
  appName: string;
  /** See {@link ProjectEnvironment}: what the branch label's age is read from. */
  createdAt?: string;
  revisionDeployedAt?: string;
  /** See {@link ProjectEnvironment}. */
  deletionTimestamp?: string;
}

/**
 * Every branch of a project, as the switcher lists them, out of the same
 * project-wide Application list {@link findApplicationForBranch} searches.
 *
 * Built on {@link toEnvironment} rather than reading the labels again: "which
 * branch is this Application?" is a question that must have exactly one answer
 * in this module, or a switcher entry would point at a page that finds nothing.
 *
 * Sorted by {@link compareBranches}: by tier (prod, the staging branches,
 * dev, then the ephemeral ones), each tier with a plain string comparison and not
 * `localeCompare` — the order has to be the same for every reader and in every
 * test run, and the collation of `localeCompare` depends on the locale.
 */
export function branchOptions(apps: ArgoApplication[]): BranchOption[] {
  return apps
    .map(toEnvironment)
    .filter(
      // '?' is what toEnvironment answers when neither the label nor
      // targetRevision named a branch; '' is a label present but empty. Both
      // would produce a nameless row linking to a page that cannot load, so
      // they are dropped rather than rendered.
      env => env.branch !== '' && env.branch !== '?',
    )
    .map(env => ({
      branch: env.branch,
      health: env.healthStatus,
      appName: env.appName,
      createdAt: env.createdAt,
      revisionDeployedAt: env.revisionDeployedAt,
      deletionTimestamp: env.deletionTimestamp,
    }))
    .sort((a, b) => compareBranches(a.branch, b.branch));
}

/** Every message key {@link healthLabel} and {@link syncLabel} can ask for. */
export type ArgoStatusMessageKey =
  | (typeof HEALTH_LABEL_KEYS)[keyof typeof HEALTH_LABEL_KEYS]
  | (typeof SYNC_LABEL_KEYS)[keyof typeof SYNC_LABEL_KEYS];

/**
 * Just enough of a translation function for the two lookups below. Taking it as
 * an argument, rather than calling `useTranslationRef` in this module, is what
 * keeps these helpers callable from a test and shareable between two screens.
 *
 * Callers pass `key => t(key)` and not `t` itself, on purpose: the translation
 * function is generic over *every* key of the ref, some of which take mandatory
 * interpolation values, so TypeScript widens its options parameter to a
 * required one and refuses the assignment. Wrapping it in a lambda instantiates
 * the generic on the status keys alone, where the options are optional — and it
 * does so with the compiler still checking, which a cast would not.
 */
export type ArgoStatusTranslator = (key: ArgoStatusMessageKey) => string;

/**
 * Both fall back to the raw ArgoCD value, and that fallback is the point: these
 * strings come straight from the ArgoCD API, so an upgrade that introduces a
 * status must degrade to showing it in English rather than to an empty cell. A
 * blank status would read as "no data" when the truth is "a status we have no
 * word for yet".
 *
 * The `in` check is what makes the index cast honest — it is checked on the
 * line before, not assumed.
 */
export function healthLabel(status: string, t: ArgoStatusTranslator): string {
  return status in HEALTH_LABEL_KEYS
    ? t(HEALTH_LABEL_KEYS[status as keyof typeof HEALTH_LABEL_KEYS])
    : status;
}

export function syncLabel(status: string, t: ArgoStatusTranslator): string {
  return status in SYNC_LABEL_KEYS
    ? t(SYNC_LABEL_KEYS[status as keyof typeof SYNC_LABEL_KEYS])
    : status;
}

export interface ArgoRequestOptions {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
  /** Narrowed to the one method used, so a test can pass a two-line stub. */
  autheliaAuth: Pick<OpenIdConnectApi, 'getIdToken'>;
  /**
   * Path under the `/argocd/api` proxy route, already encoded — for instance
   * `applications/odoo-app1-dev-andreas/resource-tree`.
   */
  path: string;
  /** Translated "no Authelia session" message; this module holds no strings. */
  noIdentityMessage: string;
  /** Aborts the request, and the reading of a streamed body with it. */
  signal?: AbortSignal;
  /**
   * The `Accept` header, when the endpoint answers differently with it: the
   * pod logs follow as server-sent events with `text/event-stream` (logs.ts).
   * The proxy forwards `accept` (one of its safe headers).
   */
  accept?: string;
}

/**
 * One authenticated GET against the ArgoCD proxy, returning the raw Response.
 *
 * Raw, because not every ArgoCD endpoint answers with one JSON document: the
 * pod logs are a stream of them, read as it comes (logs.ts). Callers that do
 * want JSON go through {@link fetchArgoJson}.
 */
export async function argoApiFetch(
  options: ArgoRequestOptions,
): Promise<Response> {
  const {
    fetchApi,
    discoveryApi,
    autheliaAuth,
    path,
    noIdentityMessage,
    signal,
    accept,
  } = options;

  // Absolute backend URL: in development the frontend runs on a different port,
  // so a relative path would hit the frontend and return the index page (HTML)
  // instead of the API answer.
  const proxyUrl = await discoveryApi.getBaseUrl('proxy');

  // The proxy carries no shared ArgoCD token: the request goes out with the
  // *user's* Authelia id_token, not the Backstage one, so ArgoCD applies its
  // own RBAC to whoever is signed in.
  //
  // optional: true because these calls run on mount — a non-optional call would
  // start an interactive sign-in, and browsers block popups that are not
  // user-initiated. Without a session we get '' and throw the message below.
  const idToken = await autheliaAuth.getIdToken({ optional: true });
  if (!idToken) {
    throw new Error(noIdentityMessage);
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${idToken}`,
  };
  if (accept) {
    headers.Accept = accept;
  }
  return fetchApi.fetch(`${proxyUrl}/argocd/api/${path}`, {
    headers,
    ...(signal ? { signal } : {}),
  });
}

export interface ArgoJsonRequestOptions extends ArgoRequestOptions {
  /**
   * Translated message for a body that is not JSON, built from its first bytes.
   * A function and not a string because the body is only known here.
   */
  notJsonMessage: (body: string) => string;
  /**
   * Translated message to raise instead of the generic HTTP one when ArgoCD
   * answers 404.
   *
   * Optional, and only worth passing when the caller knows what a missing
   * resource means to the user. On a single-Application read it means the
   * branch is gone, which deserves a sentence; on a resource-tree read it is
   * just one more failure, and the generic message is the honest answer.
   */
  notFoundMessage?: string;
}

/**
 * A non-2xx answer of ArgoCD, with its status: the environments card tells a
 * refused read (403) apart, to suggest signing in again (`tokenGroups.ts`).
 * The message is the one every caller already shows.
 */
export class ArgoHttpError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ArgoHttpError';
    this.status = status;
  }
}

/**
 * {@link argoApiFetch} plus the status check and the parsing every JSON
 * endpoint needs.
 *
 * The body is read as text *before* being parsed on purpose: an ingress or a
 * proxy answering with an HTML error page has to produce a message that says
 * so, not a bare "Unexpected token <".
 */
export async function fetchArgoJson<T>(
  options: ArgoJsonRequestOptions,
): Promise<T> {
  const response = await argoApiFetch(options);
  const body = await response.text();

  if (!response.ok) {
    if (response.status === 404 && options.notFoundMessage) {
      throw new Error(options.notFoundMessage);
    }
    throw new ArgoHttpError(
      response.status,
      `ArgoCD API: HTTP ${response.status}${
        body ? ` — ${body.slice(0, 200)}` : ''
      }`,
    );
  }

  try {
    return JSON.parse(body) as T;
  } catch {
    throw new Error(options.notJsonMessage(body.slice(0, 120)));
  }
}
